import { LockKeyholeOpen } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';
import { ImCross } from "react-icons/im";
import { useDispatch } from 'react-redux';
import { setSubLoading } from '../../Redux/Services/AuthSlice';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import { ApiCall, encryptPassword } from '../CommonComponents/CustomHooks';
import { AutoCompleteBoxNew, FormButton, InputBox } from '../CommonComponents/InputBox';

function ResetPassword() {
    const [resetPasswordData, setResetPasswordData] = useState({ userCode: '', newPassword: '', confirmPassword: '' });
    const [showPassword, setShowPassword] = useState({});
    const [policyValidations, setPolicyValidations] = useState({
        noWhitespace: false,
        length: false,
        matchConfirmPassword: false,
        complexity: false
    });
    const inputrefs = useRef([]);
    const dispatch = useDispatch();

    const handleSubmit = async () => {
        await dispatch(setSubLoading(true));
        let data = await ApiCall(`/api/User/ResetPassword`, {
            ...resetPasswordData,
            newPassword: (resetPasswordData.newPassword),
            confirmPassword: (resetPasswordData.confirmPassword)
        });
        await dispatch(setSubLoading(false));
        await NotificationSound(data.status, data.message, data.focus, inputrefs, handleReset);
    };

    const handleReset = () => {
        setResetPasswordData({ userCode: '', newPassword: '', confirmPassword: '' });
        setPolicyValidations({
            noWhitespace: false,
            length: false,
            matchConfirmPassword: false,
            complexity: false
        });
        setShowPassword({});
        inputrefs.current['txtUserCode'].focus();
    };

    const validatePassword = (password, confirmPassword) => {
        if (password !== '') {
            const noWhitespace = !/\s/.test(password);
            const length = password.length >= 8 && password.length <= 15;
            const complexity = /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password) && /[!@#$%^&*(),.?":{}|<>]/.test(password);
            setPolicyValidations({
                noWhitespace,
                length,
                matchConfirmPassword: password === confirmPassword,
                complexity
            });
        } else {
            setPolicyValidations({
                noWhitespace: false,
                length: false,
                matchConfirmPassword: false,
                complexity: false
            });
        }
    };

    const handlePasswordChange = (field, value) => {
        setResetPasswordData({ ...resetPasswordData, [field]: value });
        if (field === 'newPassword') {
            validatePassword(value, resetPasswordData.confirmPassword);
        } else if (field === 'confirmPassword') {
            validatePassword(resetPasswordData.newPassword, value);
        }
    };

    const handleTogglePassword = (field) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    useEffect(() => {
        inputrefs.current['txtUserCode']?.focus();
        document.title = 'Reset Password';
    }, []);

    return (
        <div className='bg-secondary-50 shadow-lg rounded-md p-3'>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <LockKeyholeOpen className="w-5 h-5 text-primary-600" />
                    <h2 className="text-xl font-semibold text-primary-600">Reset Password</h2>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-1/3 space-y-3">
                    <AutoCompleteBoxNew
                        id="txtUserCode"
                        placeholder="User"
                        label='User'
                        inputrefs={inputrefs}
                        required
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { inputrefs.current["txtNewPassword"].focus(); };
                        }}
                        autocompleteType='GetUserCode'
                        value={resetPasswordData.userCode || ''}
                        onChange={async (event, newInputValue) => {
                            newInputValue = newInputValue || ''
                            if (event.type !== 'keydown') {
                                setResetPasswordData({ ...resetPasswordData, userCode: newInputValue })
                            }
                        }}
                        maxLength='50'
                    />
                    <div className="relative mb-2">
                        <InputBox
                            required={true}
                            maxLength="15"
                            label="New Password"
                            placeholder="Enter New Password"
                            inputrefs={inputrefs}
                            id="txtNewPassword"
                            type={showPassword.newPassword ? 'text' : 'password'}
                            value={resetPasswordData.newPassword}
                            onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && inputrefs.current['txtConfirmPassword'].focus()}
                        />
                        <span
                            className="absolute right-3 top-[25px] cursor-pointer text-gray-500 hover:text-gray-700"
                            onClick={() => handleTogglePassword('newPassword')}
                        >
                            {showPassword.newPassword ? <FaEyeSlash className='text-blueled' /> : <FaEye className='text-blueled' />}
                        </span>
                    </div>

                    {/* Confirm Password */}
                    <div className="relative mb-2">
                        <InputBox
                            required={true}
                            label="Confirm Password"
                            placeholder="Enter Confirm Password"
                            inputrefs={inputrefs}
                            id="txtConfirmPassword"
                            maxLength="15"
                            type={showPassword.confirmPassword ? 'text' : 'password'}
                            value={resetPasswordData.confirmPassword}
                            onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && inputrefs.current['btnSub'].focus()}
                        />
                        <span
                            className="absolute right-3 top-[25px] cursor-pointer text-gray-500 hover:text-gray-700"
                            onClick={() => handleTogglePassword('confirmPassword')}
                        >
                            {showPassword.confirmPassword ? <FaEyeSlash className='text-blueled' /> : <FaEye className='text-blueled' />}
                        </span>
                    </div>

                    {/* Buttons */}
                    <FormButton
                        id="btnSub"
                        inputrefs={inputrefs}
                        handleSubmit={handleSubmit}
                        Export={false}
                        handleClear={handleReset}
                    />
                </div>

                {/* Right Side - Password Policy */}
                <div className="w-full md:w-2/3 border border-gray-300 rounded-lg">
                    <div className="bg-blueled text-white py-2 px-4 rounded-t-lg">
                        <h6 className="text-sm font-semibold m-0">Password Policy</h6>
                    </div>
                    <div className="p-4">
                        <ol className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-center gap-2">
                                <span>White spaces not allowed.</span>
                                {policyValidations.noWhitespace ? (
                                    <FaCheck className="text-green-500" />
                                ) : (
                                    <ImCross className="text-red-500 text-xs" />
                                )}
                            </li>
                            <li className="flex items-center gap-2">
                                <span>Password must be 8 to 15 character(s).</span>
                                {policyValidations.length ? (
                                    <FaCheck className="text-green-500" />
                                ) : (
                                    <ImCross className="text-red-500 text-xs" />
                                )}
                            </li>
                            <li className="flex items-center gap-2">
                                <span>
                                    Password must include at least one numeric, one uppercase, one lowercase, and one special character
                                    [$#!()].
                                </span>
                                {policyValidations.complexity ? (
                                    <FaCheck className="text-green-500" />
                                ) : (
                                    <ImCross className="text-red-500 text-xs" />
                                )}
                            </li>
                            <li className="flex items-center gap-2">
                                <span>New password and confirm password should be the same.</span>
                                {policyValidations.matchConfirmPassword ? (
                                    <FaCheck className="text-green-500" />
                                ) : (
                                    <ImCross className="text-red-500 text-xs" />
                                )}
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
